import { GoogleMap } from '@react-google-maps/api'
import {
  postCoordsValidate,
  useContentful,
  UseQueryResult,
  useRemoteConfig
} from '@smuchile/pkg-unimarc-hooks'
import { Backbone } from '@smuchile/pkg-unimarc-components'
import { getGlobalStyle } from '@smuchile/pkg-unimarc-components/helpers'
import React, { useState, useRef, useCallback, useEffect } from 'react'
// Define Libraries type locally because it's not exported by @react-google-maps/api

interface MapProps {
  location: {
    latMap: number
    lngMap: number
  }
  coordsError: (error?: string, type?: string) => void
  setButtonStatus: (status: string) => void
  setNewLocation: (lat: number, lng: number) => void
}

interface ContentfulLocalPinAsset {
  fields: {
    file: {
      url: string
    }
    title: string
  }
}

interface UseContentfulLocalPinResult {
  items: ContentfulLocalPinAsset[]
}

export const Map = ({
  location,
  coordsError,
  setButtonStatus,
  setNewLocation
}: MapProps): React.ReactElement => {
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [showDistanceModal, setShowDistanceModal] = useState(false)
  const [showAutoModal, setShowAutoModal] = useState(true)
  // Eliminamos el cierre automático del modal
  const pinDivRef = useRef<HTMLDivElement | null>(null)
  const initialCenterRef = useRef<{ lat: number; lng: number } | null>(null)
  const { data } = useContentful({
    environment: 'master',
    options: {
      'fields.title[in]': 'unimarcSLPin'
    },
    reactQuery: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false
    },
    type: 'assets'
  }) as UseQueryResult<UseContentfulLocalPinResult>
  const pinMap = data?.items?.filter((item) => {
    return item.fields.title == 'unimarcSLPin'
  })[0]?.fields?.file

  const [rangePin, setRangePing] = useState<number | null>(null)
  const { getNumber, isReady } = useRemoteConfig()

  useEffect(() => {
    if (!isReady) return
    const value = getNumber('RANGE_PIN')
    setRangePing(value)
  }, [isReady])

  const validateCoordinates = async (lat: number, lng: number) => {
    const coordsVal = await postCoordsValidate({
      data: [lng, lat]
    })
    // setNewLocation(lat, lng) intentionally disabled pending UX validation
    if (coordsVal.data.error === true) {
      return coordsError(
        'Lo sentimos, tuvimos un error al cargar tu información por favor intenta de nuevo.',
        'error'
      )
    }

    return coordsError()
  }

  const onMapIdle = useCallback(() => {
    if (!map) return
    const center = map.getCenter()
    const lat = center.lat()
    const lng = center.lng()
    const base = initialCenterRef.current
    const tooFar = isDistanceExceeded(lat, lng, base.lat, base.lng)
    if (tooFar) {
      setButtonStatus('disabled')

      // If user drags too far: do not persist new location; show modal
      if (rangePin && !showDistanceModal) setShowDistanceModal(true)
    } else {
      setButtonStatus('')
    }
    // Save as last valid center before validating coordinates (commented legacy logic)
    // setLastValidCenter({ lat, lng })
    setNewLocation(lat, lng)
    // Debug removed: latitude/longitude change log
    validateCoordinates(lat, lng)
  }, [map, showDistanceModal, location.latMap, location.lngMap])

  const onLoad = useCallback((map) => {
    initialCenterRef.current = { lat: location.latMap, lng: location.lngMap }
    setMap(map)
  }, [])

  const onUnmount = useCallback(() => {
    setMap(null)
  }, [])

  const isGoogleMapsReady =
    typeof window !== 'undefined' && !!window.google?.maps

  const isDistanceExceeded = (
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number
  ): boolean => {
    const maxDistance = Number(rangePin) || 0
    const g = (
      typeof window !== 'undefined'
        ? (window as unknown as {
            google?: typeof google
          })
        : undefined
    )?.google
    if (g?.maps?.geometry?.spherical?.computeDistanceBetween) {
      const p1 = new g.maps.LatLng(lat1, lng1)
      const p2 = new g.maps.LatLng(lat2, lng2)
      const distance = g.maps.geometry.spherical.computeDistanceBetween(p1, p2)
      return distance > maxDistance
    }
    return false
  }

  if (!isGoogleMapsReady) {
    return (
      <Backbone
        backgroundColor={getGlobalStyle('--color-loaders-background')}
        borderRadius={null}
        foregroundColor={getGlobalStyle('--color-loaders-fore-ground')}
        height={224}
        speed={3}
        type='Generic'
        width={500}
      />
    )
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '300px' }}>
      {showAutoModal && (
        <div
          style={{
            position: 'absolute',
            top: '40px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#111',
            borderRadius: '12px',
            padding: '4px 8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
            fontFamily: 'inherit',
            fontSize: '15px',
            color: '#fff',
            zIndex: 30,
            minWidth: '260px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            flexDirection: 'column'
          }}
        >
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              textAlign: 'center'
            }}
          >
            <span style={{ flex: 1 }}>
              Puede mover el mapa para ajustar la ubicación exacta.
            </span>
            <button
              aria-label='Cerrar'
              onClick={() => {
                return setShowAutoModal(false)
              }}
              style={{
                background: 'none',
                border: 'none',
                color: 'inherit',
                cursor: 'pointer',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 35,
                height: 35
              }}
              type='button'
            >
              <svg
                fill='none'
                height='24'
                viewBox='0 0 20 20'
                width='24'
                xmlns='http://www.w3.org/2000/svg'
              >
                <line
                  stroke='#fff'
                  strokeLinecap='round'
                  strokeWidth='1.5'
                  x1='5.70711'
                  x2='14.7071'
                  y1='5.29289'
                  y2='14.2929'
                />
                <line
                  stroke='#fff'
                  strokeLinecap='round'
                  strokeWidth='1.5'
                  x1='5.29289'
                  x2='14.2929'
                  y1='14.2929'
                  y2='5.29289'
                />
              </svg>
            </button>
          </div>
          {/* Punta centrada */}
          <div
            style={{
              position: 'absolute',
              left: '50%',
              bottom: '-10px',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '10px solid transparent',
              borderRight: '10px solid transparent',
              borderTop: '10px solid #111',
              zIndex: 31
            }}
          />
        </div>
      )}
      <GoogleMap
        center={{
          lat: location.latMap,
          lng: location.lngMap
        }}
        mapContainerStyle={{
          width: '100%',
          height: '100%'
        }}
        onIdle={onMapIdle}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={{
          fullscreenControl: false,
          mapTypeControl: false,
          rotateControl: false,
          scaleControl: false,
          streetViewControl: false,
          zoomControl: false
        }}
        zoom={15}
      ></GoogleMap>
      {/* Fixed central pin */}
      <div
        ref={pinDivRef}
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -100%)',
          zIndex: 10,
          pointerEvents: 'none'
        }}
      >
        {pinMap && (
          <img
            alt='Pin ubicación'
            src={pinMap?.url}
            style={{ width: '30px', height: 'auto' }}
          />
        )}
        {!pinMap && (
          <div
            style={{
              width: '20px',
              height: '30px',
              backgroundColor: '#e30613',
              borderRadius: '50% 50% 50% 0',
              transform: 'rotate(-45deg)'
            }}
          />
        )}
      </div>
      {showDistanceModal && (
        <div
          style={{
            position: 'absolute',
            left: '16px',
            bottom: '16px',
            background: '#fff',
            borderRadius: '12px',
            padding: '12px 16px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontFamily: 'inherit',
            maxWidth: '380px',
            zIndex: 20
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'row', gap: 8 }}>
            <span style={{ fontSize: 13, lineHeight: '16px', color: '#222' }}>
              📍 ¿El pin y la dirección no coinciden?
            </span>
            <button
              onClick={() => {
                setNewLocation(
                  initialCenterRef.current.lat,
                  initialCenterRef.current.lng
                )
                setShowDistanceModal(false)
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#0070f3',
                padding: 0,
                cursor: 'pointer',
                fontSize: 13,
                textDecoration: 'underline',
                alignSelf: 'flex-start'
              }}
              type='button'
            >
              Ajustar
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
